import { useEffect, useMemo, useRef, useState } from "react";
import { ProjectBar } from "@/components/ProjectBar";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Download, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { useProject } from "@/lib/workflow/useProject";
import { newId } from "@/lib/workflow/ids";
import { describeImport, exportRecords, importRecords, templateCsv } from "@/lib/workflow/layouts";
import type { Session } from "@/lib/workflow/types";
import { daysOf, durationMinutes, findClashes, formatDuration, migrateLegacySlots, scheduleText, shiftDay, sortSessions, toMinutes } from "@/lib/workflow/sessionOps";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: "Builds the schedule for your event, on one day or several, with start and end times, rooms and presenters. It warns you when two sessions overlap in the same room or with the same person, and it shares the same agenda as the other event tools in your project.",
  how: "Add sessions with a start time and a title, and optionally an end, room and presenter. Pick the day at the top, or add another day. Use 'Move times' when the whole day slips. Download Excel or CSV, copy as text, or import a CSV from a spreadsheet.",
  tip: "Give every session an end time. That is what lets the tool catch clashes and show how long each one runs. Save the project file so the agenda comes back next time and the other event tools can use it.",
};

const LEGACY = { name: "fdp-EventDaySchedule-eventName-v1", date: "fdp-EventDaySchedule-eventDate-v1", slots: "fdp-EventDaySchedule-slots-v1", next: "fdp-EventDaySchedule-nextId-v1" };
const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const dayLabel = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : iso;
};

export default function EventDaySchedule() {
  const [project, store] = useProject();
  const sessions = project.data.sessions;
  const { logUsageNow } = useLogUsageOnAction();
  const [chosenDay, setChosenDay] = useState<string | null>(null);
  const [newDay, setNewDay] = useState("");
  const [shift, setShift] = useState("15");
  const [report, setReport] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Bring over a schedule saved by the earlier version of this tool, once.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(LEGACY.slots);
      if (raw && store.get().data.sessions.length === 0) {
        const date = JSON.parse(sessionStorage.getItem(LEGACY.date) ?? '""') as string;
        const migrated = migrateLegacySlots(JSON.parse(raw), /^\d{4}-\d{2}-\d{2}$/.test(date) ? date : todayIso(), () => newId("s"));
        if (migrated.length) { store.setRecords("sessions", migrated); toast.success(`Brought over ${migrated.length} time slots from your earlier schedule`); }
      }
      Object.values(LEGACY).forEach((k) => sessionStorage.removeItem(k));
    } catch { /* storage blocked or unreadable: start empty */ }
  }, [store]);

  const days = useMemo(() => daysOf(sessions), [sessions]);
  const day = chosenDay && (days.includes(chosenDay) || days.length === 0) ? chosenDay : (days[0] ?? todayIso());
  const dayList = useMemo(() => sortSessions(sessions.filter((s) => s.day === day)), [sessions, day]);
  const clashes = useMemo(() => findClashes(sessions), [sessions]);
  const dayClashes = clashes.filter((c) => c.a.day === day);

  const update = (id: string, patch: Partial<Session>) => {
    const s = sessions.find((x) => x.id === id);
    if (!s) return;
    const next = { ...s, ...patch } as Session;
    for (const k of Object.keys(next) as (keyof Session)[]) if (next[k] === undefined || next[k] === "") if (k !== "title") delete next[k];
    store.upsertRecords("sessions", [next]);
  };
  const remove = (id: string) => store.setRecords("sessions", sessions.filter((s) => s.id !== id));
  const add = () => {
    const last = dayList[dayList.length - 1];
    const start = last?.end ?? last?.start ?? "09:00";
    store.upsertRecords("sessions", [{ id: newId("s"), title: "", day, start }]);
    setChosenDay(day);
  };
  const addDay = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDay)) { toast.error("Pick a date first."); return; }
    setChosenDay(newDay); setNewDay("");
    if (!sessions.some((s) => s.day === newDay)) store.upsertRecords("sessions", [{ id: newId("s"), title: "", day: newDay, start: "09:00" }]);
  };
  const move = () => {
    const n = Math.round(Number(shift));
    if (!Number.isFinite(n) || n === 0) { toast.error("Enter the minutes to move, such as 15 or -30."); return; }
    const r = shiftDay(sessions, day, n);
    store.setRecords("sessions", r.sessions);
    toast.success(r.clamped ? `Moved by ${n} minutes. ${r.clamped} time${r.clamped > 1 ? "s were" : " was"} held at the edge of the day.` : `Moved this day by ${n} minutes.`);
  };

  const complete = sessions.filter((s) => s.title.trim());
  const usable = complete.length > 0;
  const noEventName = !project.name.trim();

  const copy = async () => {
    if (!usable) { toast.error("Add at least one session with a title first."); return; }
    if (!(await logUsageNow())) return;
    await navigator.clipboard.writeText(scheduleText(complete, project.name, dayLabel));
    toast.success("Schedule copied to clipboard");
  };
  const exportExcel = async () => {
    if (!usable) { toast.error("Add at least one session with a title first."); return; }
    if (!(await logUsageNow())) return;
    const head = ["Day", "Start", "End", "Length", "Session", "Room", "Presenter", "Notes"];
    const rows: (string | number)[][] = [[project.name], [], head,
      ...sortSessions(complete).map((s) => { const d = durationMinutes(s); return [s.day, s.start, s.end ?? "", d ? formatDuration(d) : "", s.title, s.room ?? "", s.presenter ?? "", s.notes ?? ""]; })];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 12 }, { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 40 }, { wch: 16 }, { wch: 18 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Schedule");
    downloadBlob(new Blob([XLSX.write(wb, { type: "array", bookType: "xlsx" })], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "event-schedule.xlsx");
    toast.success("Schedule downloaded!");
  };
  const exportCsv = async () => {
    if (!usable) { toast.error("Add at least one session with a title first."); return; }
    if (!(await logUsageNow())) return;
    downloadBlob(new Blob([exportRecords("sessions", sortSessions(complete))], { type: "text/csv;charset=utf-8" }), "event-schedule.csv");
    toast.success("Schedule downloaded");
  };
  const importFile = async (f: File | undefined) => {
    if (!f) return;
    const r = importRecords("sessions", await f.text(), { currency: project.currency });
    const errors = r.issues.filter((i) => i.level === "error");
    if (r.records.length) {
      const byId = new Map(sessions.map((s) => [s.id, s]));
      r.records.forEach((s) => byId.set(s.id, s));
      store.setRecords("sessions", Array.from(byId.values()));
      toast.success(describeImport(r, "sessions"));
    } else toast.error(errors[0]?.message ?? "Nothing could be imported");
    setReport(r.issues.length ? r.issues.slice(0, 8).map((i) => `Row ${i.row}: ${i.message}`).join("\n") : null);
  };

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mb-5">
        <div>
          <Label className="mb-1 block">Event name *</Label>
          <Input value={project.name} maxLength={120} aria-label="Event name" onChange={(e) => store.set((p) => ({ ...p, name: e.target.value }))} />
          {noEventName && <NoticeBox className="text-xs mt-1">Give the event a name for the copy and downloads.</NoticeBox>}
        </div>
        <div>
          <Label className="mb-1 block">Day</Label>
          <div className="flex gap-2">
            <select className="border rounded px-2 py-2 w-full bg-background text-sm" aria-label="Day" value={day} onChange={(e) => setChosenDay(e.target.value)} data-testid="eds-day">
              {(days.length ? days : [day]).map((d) => <option key={d} value={d}>{dayLabel(d)}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2 items-end sm:col-span-2">
          <div className="flex-1"><Label className="mb-1 block text-xs">Add another day</Label><Input type="date" value={newDay} onChange={(e) => setNewDay(e.target.value)} aria-label="New day" data-testid="eds-newday" /></div>
          <Button variant="outline" onClick={addDay} data-testid="eds-addday">Add day</Button>
        </div>
      </div>

      <div className="space-y-3" data-testid="eds-rows">
        {dayList.map((s) => {
          const d = durationMinutes(s);
          return (
            <div key={s.id} className="flex flex-wrap items-center gap-2 border-b pb-3">
              <Input className="w-[7.5rem]" type="time" aria-label="Start" value={s.start} onChange={(e) => e.target.value && update(s.id, { start: e.target.value })} />
              <Input className="w-[7.5rem]" type="time" aria-label="End" value={s.end ?? ""} onChange={(e) => update(s.id, { end: e.target.value || undefined })} />
              <Input className="flex-1 min-w-[10rem]" aria-label="Session title" placeholder="What's happening..." maxLength={300} value={s.title} onChange={(e) => update(s.id, { title: e.target.value })} />
              <Input className="w-32" aria-label="Room" placeholder="Room" maxLength={120} value={s.room ?? ""} onChange={(e) => update(s.id, { room: e.target.value })} />
              <Input className="w-36" aria-label="Presenter" placeholder="Presenter" maxLength={120} value={s.presenter ?? ""} onChange={(e) => update(s.id, { presenter: e.target.value })} />
              {d && <span className="text-xs text-muted-foreground w-16">{formatDuration(d)}</span>}
              <Button size="icon" variant="ghost" aria-label="Remove session" onClick={() => remove(s.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          );
        })}
        {dayList.length === 0 && <p className="text-sm text-muted-foreground">No sessions on this day yet.</p>}
      </div>

      {dayClashes.length > 0 && (
        <NoticeBox role="alert" testId="eds-clash" className="mt-3 text-sm space-y-1">
          <p className="font-medium flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> {dayClashes.length} overlap{dayClashes.length > 1 ? "s" : ""} on this day</p>
          {dayClashes.map((c, i) => (
            <p key={i}>{c.a.start} {c.a.title || "(untitled)"} and {c.b.start} {c.b.title || "(untitled)"} overlap in the same {c.why === "room" ? `room (${c.a.room})` : `presenter (${c.a.presenter})`}.</p>
          ))}
        </NoticeBox>
      )}

      <div className="flex flex-wrap gap-2 mt-4 items-end">
        <Button variant="outline" onClick={add} data-testid="eds-add"><Plus className="h-4 w-4 mr-2" /> Add session</Button>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Move this day (minutes)</Label><Input className="w-24" inputMode="numeric" value={shift} onChange={(e) => setShift(e.target.value.replace(/[^\d-]/g, ""))} aria-label="Minutes to move" /></div>
          <Button variant="outline" onClick={move} data-testid="eds-move">Move times</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button onClick={copy} disabled={!usable}>Copy schedule</Button>
        <Button variant="outline" onClick={exportExcel} disabled={!usable} className="gap-2"><Download className="h-4 w-4" /> Download Excel</Button>
        <Button variant="outline" onClick={exportCsv} disabled={!usable} className="gap-2"><Download className="h-4 w-4" /> Download CSV</Button>
        <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2"><Upload className="h-4 w-4" /> Import CSV</Button>
        <Button variant="ghost" size="sm" onClick={() => downloadBlob(new Blob([templateCsv("sessions")], { type: "text/csv;charset=utf-8" }), "schedule-template.csv")}>Blank CSV</Button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { void importFile(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
      {report && <pre className="mt-3 text-xs whitespace-pre-wrap rounded border p-3 bg-muted/30">{report}</pre>}

      <div className="mt-6 rounded-lg border bg-muted/30 p-4" data-testid="eds-preview">
        <p className="text-sm font-medium mb-1">Preview (sorted by time)</p>
        <p className="text-xs text-muted-foreground mb-2">{dayLabel(day)}</p>
        <div className="space-y-1.5">
          {dayList.filter((s) => toMinutes(s.start) !== null).map((s) => (
            <div key={s.id} className="flex gap-3 text-sm">
              <span className="font-mono text-muted-foreground w-28 shrink-0">{s.end ? `${s.start}-${s.end}` : s.start}</span>
              <span>{s.title}{[s.room, s.presenter].filter(Boolean).length > 0 && <span className="text-muted-foreground"> ({[s.room, s.presenter].filter(Boolean).join(", ")})</span>}</span>
            </div>
          ))}
        </div>
      </div>
      <ProjectBar />
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
